import React, { useEffect } from 'react'; // [수정] useEffect 추가
import { 
  Container, Box, Typography, Button, Paper, 
  Stack, Divider, CircularProgress
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReplayIcon from '@mui/icons-material/Replay';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const StudyResult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const THEME_NAVY = '#1a233a';

  // [추가] 컴포넌트 렌더링 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // [수정] 배점 정보를 포함한 데이터 수신
  const { 
    totalCount = 0, 
    correctCount = 0, 
    categoryName = "수어 학습",
    totalScore = 0,  
    earnedScore = 0  
  } = location.state || {};

  // 백분율 계산 (배점 기준)
  const percentage = totalScore > 0 ? Math.round((earnedScore / totalScore) * 100) : 0;

  const getResultMessage = () => {
    if (percentage === 100) return { text: "완벽합니다! 모든 문제를 맞히셨어요.", color: "#4caf50" };
    if (percentage >= 70) return { text: "훌륭해요! 실력이 눈에 띄게 좋아졌네요.", color: "#2196f3" };
    return { text: "조금만 더 힘내볼까요? 반복 학습이 중요해요.", color: "#ff9800" };
  };

  const resultMessage = getResultMessage();

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 4, sm: 8 }, mb: 4 }}>
      <Paper 
        elevation={10} 
        sx={{ borderRadius: 5, overflow: 'hidden', textAlign: 'center', bgcolor: '#fff' }}
      >
        {/* 상단 장식 영역 */}
        <Box sx={{ bgcolor: THEME_NAVY, py: 4, color: '#fff' }}>
          <EmojiEventsIcon sx={{ fontSize: 80, color: '#ffd700', mb: 1 }} />
          <Typography variant="h5" fontWeight="800">학습 완료!</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>{categoryName}</Typography>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          {/* 점수 표시 (배점 기반 도넛 차트) */}
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
            <CircularProgress 
              variant="determinate" 
              value={percentage} 
              size={150} 
              thickness={4.5} 
              sx={{ color: resultMessage.color, strokeLinecap: 'round' }} 
            />
            <Box
              sx={{
                top: 0, left: 0, bottom: 0, right: 0,
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h3" fontWeight="900" color={THEME_NAVY}>
                {earnedScore}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, mt: -0.5 }}>
                / {totalScore} 점
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, px: 2 }}>
            {resultMessage.text}
          </Typography>
          
          <Divider sx={{ my: 3 }} />

          {/* 세부 수치 요약 - 반응형 수정 영역 */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} // 모바일은 세로, PC는 가로
            spacing={2} 
            justifyContent="center" 
            sx={{ mb: 4 }}
          >
            <Box sx={{ 
              flex: 1, 
              px: 2, 
              py: 2, 
              bgcolor: '#f1f8e9', 
              borderRadius: 3, 
              border: '1px solid #c5e1a5',
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' }, 
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'center' }
            }}>
              <Typography variant="caption" color="#2e7d32" fontWeight="bold">정답 개수</Typography>
              <Typography variant="h6" color="#2e7d32" fontWeight="900">
                <CheckCircleIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                {correctCount}
              </Typography>
            </Box>

            <Box sx={{ 
              flex: 1, 
              px: 2, 
              py: 2, 
              bgcolor: '#fffde7', 
              borderRadius: 3, 
              border: '1px solid #fff59d',
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'center' }
            }}>
              <Typography variant="caption" color={THEME_NAVY} fontWeight="bold">정답률</Typography>
              <Typography variant="h6" color={THEME_NAVY} fontWeight="900">
                {percentage}%
              </Typography>
            </Box>

            <Box sx={{ 
              flex: 1, 
              px: 2, 
              py: 2, 
              bgcolor: '#ffebee', 
              borderRadius: 3, 
              border: '1px solid #ffcdd2',
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'center' }
            }}>
              <Typography variant="caption" color="#c62828" fontWeight="bold">오답 개수</Typography>
              <Typography variant="h6" color="#c62828" fontWeight="900">
                <CancelIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                {totalCount - correctCount}
              </Typography>
            </Box>
          </Stack>

          {/* 버튼 영역 */}
          <Stack spacing={2}>
            <Button 
              variant="contained" 
              size="large" 
              startIcon={<DashboardIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{ 
                bgcolor: THEME_NAVY, 
                py: 1.8, 
                borderRadius: 2.5,
                fontWeight: 'bold',
                fontSize: '1rem',
                '&:hover': { bgcolor: '#2c3e50' }
              }}
            >
              대시보드로 이동
            </Button>
            <Button 
              variant="outlined" 
              size="large" 
              startIcon={<ReplayIcon />}
              onClick={() => navigate('/category-select')} 
              sx={{ 
                color: THEME_NAVY, 
                borderColor: THEME_NAVY,
                py: 1.8, 
                borderRadius: 2.5,
                fontWeight: 'bold',
                fontSize: '1rem',
                '&:hover': { borderColor: '#2c3e50', bgcolor: '#f4f6f8' }
              }}
            >
              다시 학습하기
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default StudyResult;